import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import ModalV2 from '/@/components/bayer/modal-v2';
import Spinner from '/@/components/spinner';
import { useCatalogProductById } from '/@/hooks/catalog';
import { _ } from '/@/locale';
import {
    useCreateYieldEstimationMutation,
    useUpdateYieldEstimationMutation,
} from '/@/store/crop-monitoring/apiProviders';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';
import YieldEstimationGuide from '../yield-estimation-guide';
import { buildEstimation } from './calc';
import YieldEstimationFormset from './components/formset';
import YieldEstimationResult from './components/result';

export interface YieldSampleAPI {
    spikesPerHectare: number;
    grainsPerSpike: number;
    thousandGrainsWeight: number;
    result?: number;
    name: string;
}
export interface YieldEstimationAPI {
    id?: number;
    cropMonitoring: number;
    status: 'draft' | 'saved' | 'archived';
    result?: number;
    yieldSamples: YieldSampleAPI[];
}

export type YieldEstimationForm = {
    samples: {
        name: string;
        spikes: number | '';
        grainsSpike: number | '';
        weight1000Grains: number | '';
    }[];
};

const YieldEstimationForm = ({
    cropMonitoring,
    setYieldEstimationDrawerIsOpen,
    onRequestClose,
    onDirtyChange,
    open,
    initialStep = 'form',
    estimationId = null,
}: {
    cropMonitoring: CropMonitoring;
    setYieldEstimationDrawerIsOpen: (bool: boolean) => void;
    /** Ask the parent to close; the parent decides whether to confirm based on reported dirtiness. */
    onRequestClose: () => void;
    /** Report whether the form currently has unsaved changes. */
    onDirtyChange: (dirty: boolean) => void;
    /** Whether the drawer is open — used to re-apply `initialStep` on each open. */
    open: boolean;
    /** Step to open on. `result` shows the estimation ("Resultado por muestras"). */
    initialStep?: 'form' | 'result';
    /** Estimation to view/edit on the `result` step; null => create a new one on the `form` step. */
    estimationId?: number | null;
}) => {
    const [guideModalIsOpen, setGuideModalIsOpen] = useState(false);
    const [step, setStep] = useState<'form' | 'result'>(initialStep);
    // True once the user runs an estimation this session; gates the "Guardar" button so that
    // merely viewing an existing estimation ("Resultado por muestras") doesn't offer to save.
    const [hasEstimated, setHasEstimated] = useState(false);

    // The drawer stays mounted across opens (only `open` toggles), so re-apply the requested
    // step and reset the estimated flag each time it opens.
    useEffect(() => {
        if (open) {
            setStep(initialStep);
            setHasEstimated(false);
        }
    }, [open, initialStep]);

    const createMutation = useCreateYieldEstimationMutation({ cropMonitoringId: cropMonitoring.id });
    const updateMutation = useUpdateYieldEstimationMutation({ cropMonitoringId: cropMonitoring.id });
    const saving = createMutation.isMutating || updateMutation.isMutating;

    const cropData = getCropMonitoringData(cropMonitoring);
    const estimations = (cropData?.yieldEstimations ?? []) as unknown as YieldEstimationAPI[];
    // The estimation being viewed/edited (by id); undefined in create mode.
    const targetEstimation = estimationId ? estimations.find((e) => e.id === estimationId) : undefined;

    const { data: catalogProduct, isLoading: loadingCatalogProduct } = useCatalogProductById({
        id: cropMonitoring?.cornCropMonitoring?.product,
        crop: 'corn',
    });

    const weight1000GrainsFeature = catalogProduct?.features?.find((feature: { name: string; value: string }) => {
        return _(feature.name).includes('1000');
    });

    const weight1000GrainsDefaultValue = weight1000GrainsFeature
        ? Number.parseInt(_(weight1000GrainsFeature.value), 10)
        : undefined;

    const defaultValue = [
        { name: '', spikes: '', grainsSpike: '', weight1000Grains: '' },
    ] as YieldEstimationForm['samples'];

    // Prefill with the target estimation's samples when editing an existing one; blank for a new
    // estimation. (Weight is stored in kg on the API and shown in grams in the form.)
    const parsedSamples: YieldEstimationForm['samples'] = (targetEstimation?.yieldSamples ?? []).map((s) => ({
        name: s.name,
        spikes: Number(s.spikesPerHectare),
        grainsSpike: Number(s.grainsPerSpike),
        weight1000Grains: Number(s.thousandGrainsWeight) * 1000,
    }));

    const form = useForm<YieldEstimationForm>({
        defaultValues: { samples: parsedSamples.length > 0 ? parsedSamples : defaultValue },
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <form.reset is not a dependency by official docs https://react-hook-form.com/docs/useform/reset>
    useEffect(() => {
        if (!estimationId) {
            form.reset({
                samples: [
                    { name: '', spikes: '', grainsSpike: '', weight1000Grains: weight1000GrainsDefaultValue || 300 },
                ],
            });
        }
    }, [weight1000GrainsDefaultValue]);

    // Result step shows the client-computed preview once the user has run an estimation this
    // session; otherwise (pure view) the existing estimation from the crop monitoring.
    const displayEstimation = hasEstimated
        ? buildEstimation(cropMonitoring.id, form.getValues().samples)
        : targetEstimation;

    // Nothing is persisted until "Guardar": create a new estimation, or update the one being edited.
    const handleSave = async () => {
        const payload = buildEstimation(cropMonitoring.id, form.getValues().samples);
        try {
            if (estimationId) {
                await updateMutation.trigger({ ...payload, id: estimationId });
            } else {
                await createMutation.trigger(payload);
            }
            enqueueSnackbar(_('Estimación guardada correctamente'), { variant: 'success' });
            setYieldEstimationDrawerIsOpen(false);
        } catch {
            enqueueSnackbar(_('Hubo un problema al guardar la estimación. Por favor, intentá nuevamente.'), {
                variant: 'error',
            });
        }
    };

    // Report unsaved-changes so the parent's close handler can skip the confirm when clean.
    const hasUnsavedChanges = hasEstimated || form.formState.isDirty;
    // biome-ignore lint/correctness/useExhaustiveDependencies: onDirtyChange is a stable setter
    useEffect(() => {
        onDirtyChange(hasUnsavedChanges);
    }, [hasUnsavedChanges]);

    const renderFormContent = () => {
        if (step === 'form') {
            if (loadingCatalogProduct) {
                return (
                    <Stack alignItems='center' justifyContent='center' height='100%'>
                        <Spinner />
                    </Stack>
                );
            }
            return (
                <YieldEstimationFormset
                    form={form}
                    setGuideModalIsOpen={setGuideModalIsOpen}
                    onCancel={onRequestClose}
                    goNext={() => {
                        setHasEstimated(true);
                        setStep('result');
                    }}
                    weight1000GrainsDefaultValue={weight1000GrainsDefaultValue}
                />
            );
        }
        return null;
    };

    return (
        <Stack width={{ xs: 'auto', sm: '500px' }} height='100%' overflow={'hidden'}>
            <Stack
                direction={'row'}
                borderBottom={(t) => `1px solid ${t.palette.divider}`}
                p={3}
                justifyContent={'space-between'}
                alignItems={'center'}
            >
                <Typography color={'primary'} variant='subtitle1'>
                    {_('Estimación de rendimiento del cultivo')}
                </Typography>
                <IconButton onClick={onRequestClose}>
                    <CloseIcon fontSize='large' />
                </IconButton>
            </Stack>
            {renderFormContent()}
            {step === 'result' && displayEstimation && (
                <YieldEstimationResult
                    yieldEstimation={displayEstimation}
                    goBack={() => setStep('form')}
                    onSave={handleSave}
                    saving={saving}
                    showSave={hasEstimated}
                />
            )}

            <ModalV2
                open={guideModalIsOpen}
                onClose={() => {
                    setGuideModalIsOpen(false);
                }}
                actions={
                    <Button
                        color='secondary'
                        onClick={() => {
                            setGuideModalIsOpen(false);
                        }}
                    >
                        {_('Entendido')}
                    </Button>
                }
                title={_('Guía rápida para tomar muestras')}
                actionsProps={{ sx: { justifyContent: 'end' } }}
                maxWidth={'md'}
            >
                <YieldEstimationGuide />
            </ModalV2>
        </Stack>
    );
};

export default YieldEstimationForm;
